<template>
    <div v-show="mapLink" class="bg-white">
        <div>
            <div id="live-map" style="height:400px; z-index: 1" class="w-full"></div>

            <div class="flex justify-center p-4">
                <div class="flex mx-4 items-center">
                    <div class="w-4 h-4 rounded-full mr-2 bg-green-500"></div>
                    <span class="text-sm text-gray-700">verfügbar</span>
                </div>

                <div class="flex mx-4 items-center">
                    <div class="w-4 h-4 rounded-full mr-2 bg-red-500"></div>
                    <span class="text-sm text-gray-700">nicht verfügbar</span>
                </div>
            </div>
        </div>

        <div v-if="!mapLink" class="p-6">
            <p v-if="floorId" class="text-gray-500 text-center">Keine Karte für diese Etage verfügbar.</p>
            <p v-else class="text-gray-500 text-center">Bitte wählen Sie eine Etage aus, um die Karte zu sehen.</p>
        </div>

        <!-- Click Dialog Modal -->
        <div v-if="showDialog" class="fixed inset-0 z-50 flex items-center justify-center" @click.self="closeDialog">
            <div class="fixed inset-0 bg-black bg-opacity-50"></div>
            <div class="bg-white rounded-lg shadow-xl z-10 max-w-sm w-full mx-4">
                <div class="p-4 border-b">
                    <p v-if="resourceTypeLabel" class="text-xs text-gray-500 uppercase tracking-wide">{{ resourceTypeLabel }}</p>
                    <h3 class="text-lg font-semibold text-gray-900">{{ dialogItem?.name }}</h3>
                    <p v-if="dialogItem?.details" class="text-sm text-gray-500 mt-1">{{ dialogItem.details }}</p>
                </div>

                <div class="p-4 space-y-3">
                    <!-- Available Resource -->
                    <template v-if="dialogType === 'available'">
                        <div class="flex gap-2">
                            <button @click="navigateTo(dialogItem.book_url)"
                                    class="flex-1 flex items-center justify-center gap-2 px-4 py-3 text-white bg-primary hover:bg-primary/90 rounded-lg font-medium"
                                    title="Buchen">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span>Buchen</span>
                            </button>
                            <button v-if="dialogItem.can_edit" @click="navigateTo(dialogItem.edit_url)"
                                    class="flex-1 flex items-center justify-center gap-2 px-4 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium"
                                    title="Bearbeiten">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                                <span>Bearbeiten</span>
                            </button>
                        </div>
                    </template>

                    <!-- Unavailable Resource (with booking) -->
                    <template v-else-if="dialogType === 'unavailable' && !dialogItem.is_restricted">
                        <!-- Show booking info if available -->
                        <div v-if="currentBookingInfo" class="text-sm text-gray-600">
                            <p class="font-medium">Aktuelle Buchung:</p>
                            <p>{{ currentBookingInfo }}</p>
                        </div>
                        <!-- Show reason if no booking info (e.g., managed desk with owner) -->
                        <div v-else-if="unavailabilityReason" class="text-sm text-gray-600">
                            <p>{{ unavailabilityReason }}</p>
                        </div>
                        <div v-if="dialogItem.can_edit" class="flex gap-2">
                            <button @click="navigateTo(dialogItem.edit_url)"
                                    class="flex-1 flex items-center justify-center gap-2 px-4 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium"
                                    title="Bearbeiten">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                                <span>Bearbeiten</span>
                            </button>
                        </div>
                    </template>

                    <!-- Restricted/Blocked Resource -->
                    <template v-else-if="dialogType === 'unavailable' && dialogItem.is_restricted">
                        <div class="text-center py-2">
                            <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                                Gesperrt
                            </span>
                        </div>
                        <div v-if="dialogItem.can_edit" class="flex gap-2">
                            <button @click="navigateTo(dialogItem.edit_url)"
                                    class="flex-1 flex items-center justify-center gap-2 px-4 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium"
                                    title="Bearbeiten">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                                <span>Bearbeiten</span>
                            </button>
                        </div>
                    </template>
                </div>

                <div class="p-4 border-t">
                    <button @click="closeDialog"
                            class="w-full px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg font-medium">
                        Schließen
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import L from "leaflet";

export default {
    name: "LiveMap",

    props: {
        mapLink: {
            type: String,
            default: null
        },
        availableDesks: {
            type: Array,
            default: () => []
        },
        unavailableDesks: {
            type: Array,
            default: () => []
        },
        availableConferenceRooms: {
            type: Array,
            default: () => []
        },
        unavailableConferenceRooms: {
            type: Array,
            default: () => []
        },
        availableBookables: {
            type: Array,
            default: () => []
        },
        unavailableBookables: {
            type: Array,
            default: () => []
        },
        floorId: {
            default: null
        }
    },

    data() {
        return {
            map: null,
            showDialog: false,
            dialogItem: null,
            dialogType: null // 'available' or 'unavailable'
        };
    },

    computed: {
        currentBookingInfo() {
            if (!this.dialogItem) return null;

            const bookings = this.dialogItem.bookings || this.dialogItem.schedules || [];
            if (!bookings.length) return null;

            const booking = bookings[0];
            const start = new Date(booking.start);
            const end = new Date(booking.end);

            const startTime = start.toLocaleString('de-DE', {
                hour: '2-digit',
                minute: '2-digit'
            });

            const endTime = end.toLocaleString('de-DE', {
                hour: '2-digit',
                minute: '2-digit'
            });

            const userName = booking.user_or_reservation_text || '';

            if (endTime === '00:00') {
                return `${userName} - ab ${startTime} Uhr`;
            }

            return `${userName} - ${startTime} bis ${endTime} Uhr`;
        },

        unavailabilityReason() {
            if (!this.dialogItem) return null;

            // Check if it's a managed desk with an owner
            if (this.dialogItem.user) {
                const owner = this.dialogItem.user;
                const ownerName = [owner.first_name, owner.name].filter(Boolean).join(' ');
                if (ownerName) {
                    return `Fester Arbeitsplatz von ${ownerName}`;
                }
            }

            return 'Nicht verfügbar';
        },

        resourceTypeLabel() {
            if (!this.dialogItem) return '';

            const itemType = this.dialogItem.item_type;

            if (itemType === 'desk') {
                return 'Schreibtisch';
            }

            if (itemType === 'conference_room') {
                return 'Konferenzraum';
            }

            if (itemType === 'bookable') {
                return 'Weitere Ressource';
            }

            return '';
        }
    },

    mounted() {
        this.init();
    },

    beforeDestroy() {
        if (this.map) {
            this.map.off();
            this.map.remove();
            this.map = null;
        }
    },

    watch: {
        mapLink() {
            this.init();
        }
    },

    methods: {
        init() {
            if (!this.mapLink) {
                return;
            }

            if (this.map != null) {
                this.map.off();
                this.map.remove();
                this.map = null;
            }

            let img = new Image();

            img.onload = () => {
                let minZoom = -Math.ceil(Math.max(img.width + img.height) / 1000);
                this.map = L.map('live-map', {
                    crs: L.CRS.Simple,
                    renderer: L.canvas(),
                    minZoom: minZoom,
                    maxZoom: 2,
                    zoomSnap: 0.25,
                    zoomControl: false,
                    zoomAnimation: false,
                    fadeAnimation: false,
                    markerZoomAnimation: false,
                    dragging: true,
                });

                this.createMap(img.width, img.height);
            };
            img.src = this.mapLink;
        },

        createMap(width, height) {
            let bounds = [[0, 0], [height, width]];
            L.imageOverlay(this.mapLink, bounds).addTo(this.map);

            this.map.setMaxBounds(bounds);
            this.map.fitBounds(bounds);

            requestAnimationFrame(() => this.addItems());
        },

        addItems() {
            this.addAvailableItems();
            this.addUnavailableItems();
        },

        addAvailableItems() {
            this.availableDesks.forEach(item => {
                if (!item.position) return;
                let color = 'green';
                let marker = this.getMarker(item);
                let text = this.getToolTip(item, true);
                this.drawRectangle(item.id, true, item.position, color, text, marker, false, item.item_type, true);
            });

            this.availableConferenceRooms.forEach(item => {
                if (!item.position) return;
                let color = 'green';
                let marker = this.getMarker(item);
                let text = this.getToolTip(item, true);
                this.drawRectangle(item.id, true, item.position, color, text, marker, false, item.item_type, true);
            });

            this.availableBookables.forEach(item => {
                if (!item.position) return;
                let color = 'green';
                let marker = this.getMarker(item);
                let text = this.getToolTip(item, true);
                this.drawRectangle(item.id, true, item.position, color, text, marker, false, item.item_type, true);
            });
        },

        addUnavailableItems() {
            this.unavailableDesks.forEach(item => {
                if (!item.position) return;
                let color = 'red';
                let marker = this.getMarker(item);
                let text = this.getToolTip(item, false);
                const first = (item.bookings && item.bookings[0]) || (item.schedules && item.schedules[0]);
                const isCheckedIn = !!(first && first.checked_in_at);
                this.drawRectangle(item.id, true, item.position, color, text, marker, isCheckedIn, item.item_type, false);
            });

            this.unavailableConferenceRooms.forEach(item => {
                if (!item.position) return;
                let color = 'red';
                let marker = this.getMarker(item);
                let text = this.getToolTip(item, false);
                const first = (item.bookings && item.bookings[0]) || (item.schedules && item.schedules[0]);
                const isCheckedIn = !!(first && first.checked_in_at);
                this.drawRectangle(item.id, true, item.position, color, text, marker, isCheckedIn, item.item_type, false);
            });

            this.unavailableBookables.forEach(item => {
                if (!item.position) return;
                let color = 'red';
                let marker = this.getMarker(item);
                let text = this.getToolTip(item, false);
                const first = (item.bookings && item.bookings[0]) || (item.schedules && item.schedules[0]);
                const isCheckedIn = !!(first && first.checked_in_at);
                this.drawRectangle(item.id, true, item.position, color, text, marker, isCheckedIn, item.item_type, false);
            });
        },

        drawRectangle(identifier, clickable, position, color, description, marker, isCheckedIn = false, itemType = 'desk', isAvailable = true) {
            let layer;

            if (position.type === 'circle') {
                layer = L.circle(position.center, {
                    color: color,
                    weight: 1,
                    id: identifier,
                    itemType: itemType,
                    isAvailable: isAvailable
                });
            } else {
                layer = L.rectangle([
                    [position.center[0] + position.height, position.center[1] - position.width],
                    [position.center[0] - position.height, position.center[1] + position.width],
                ], {
                    color: color,
                    weight: 1,
                    id: identifier,
                    itemType: itemType,
                    isAvailable: isAvailable
                });

                this.rotateRectangle(position, layer);
            }

            layer.bindTooltip(description).addTo(this.map);

            if (clickable) {
                layer.on('click', (e) => {
                    this.handleItemClick(e.target.options.id, e.target.options.itemType, e.target.options.isAvailable);
                });
            }

            if (marker) {
                let [l, t] = position.center;
                let markerOnMap = L.marker([l, t], {
                    opacity: 0,
                    icon: L.divIcon({
                        iconAnchor: [0, 0],
                        iconSize: [0, 0],
                    }),
                });

                markerOnMap.bindTooltip(marker, {
                    permanent: true,
                    className: 'myTooltip' + (isCheckedIn ? ' checkedIn' : ''),
                    offset: [0, 0],
                    direction: 'center',
                    interactive: true
                });
                markerOnMap.addTo(this.map);
            }
        },

        rotateRectangle(position, rectangle) {
            if (!position) {
                return;
            }
            let center = rectangle.getBounds().getCenter();
            let rotationRadians = (position.angle * Math.PI) / 180;
            let rotatedCorners = rectangle.getLatLngs()[0].map((corner) => {
                return this.rotatePoint(corner, center, rotationRadians);
            });

            rectangle.setLatLngs([rotatedCorners]);
        },

        rotatePoint(point, center, angle) {
            let x = center.lng + (point.lng - center.lng) * Math.cos(angle) - (point.lat - center.lat) * Math.sin(angle);
            let y = center.lat + (point.lng - center.lng) * Math.sin(angle) + (point.lat - center.lat) * Math.cos(angle);
            return L.latLng(y, x);
        },

        getToolTip(item, available = true) {
            if (available) {
                return item.name + '<br>' + (item.details || '');
            }

            let bookings = item.bookings || item.schedules || [];
            let text = bookings ? bookings.map(booking => {

                const start = (new Date(booking.start));
                const result = start.toLocaleString('de-DE', {
                    hour: '2-digit',
                    minute: '2-digit'
                });

                const resultEnd = (new Date(booking.end)).toLocaleString('de-DE', {
                    hour: '2-digit',
                    minute: '2-digit'
                });

                let checkIn = booking.checked_in_at
                    ? new Date(booking.checked_in_at).toLocaleString(
                        'de-DE',
                        {
                            hour: '2-digit',
                            minute: '2-digit',
                        }
                    )
                    : null;

                checkIn = checkIn
                    ? ' ,eingecheckt um ' + checkIn
                    : '';

                if (resultEnd === '00:00') {
                    return 'gebucht ab ' +
                        result +
                        ' Uhr ' +
                        checkIn +
                        ' - ' +
                        booking.user_or_reservation_text;
                }

                return 'gebucht von ' +
                    result +
                    ' - ' +
                    resultEnd +
                    ' Uhr ' +
                    checkIn +
                    ' - ' +
                    booking.user_or_reservation_text;
            }).join('<br>') : '';

            if (text.length === 0) {
                text = item.owner ? 'Fester Arbeitsplatz von ' + item.owner.first_name + ' ' + item.owner.name : 'Nicht verfügbar';
            }

            return item.name + '<br>' + text + '<br>' + (item.details || '');
        },

        getMarker(item) {
            if (item._markerHtml !== undefined) return item._markerHtml;

            let marker;
            if (item.user) {
                const s = (item.user.first_name?.[0] || '') + (item.user.name?.[0] || '');
                marker = s ? s.toUpperCase() : '';
                if (item.user.avatar) {
                    marker = `<div class='avatar-div'><img class='rounded-full w-full h-full' src='${item.user.avatar}' alt='${marker}' /></div>`;
                }
            }

            const bookings = item.bookings || item.schedules || [];
            if (bookings && bookings.length) {
                const u = bookings[0]?.user;
                const s = (u?.first_name?.[0] || '') + (u?.name?.[0] || '');
                marker = s ? s.toUpperCase() : marker;
                if (u?.avatar) {
                    marker = `<div class='avatar-div'><img class='w-8 max-h-8 rounded-full' src='${u.avatar}' alt='${marker}' /></div>`;
                }
            }

            item._markerHtml = marker;
            return marker;
        },

        handleItemClick(id, type, isAvailable) {
            let item = null;

            if (isAvailable) {
                if (type === 'desk') {
                    item = this.availableDesks.find(i => i.id === id);
                } else if (type === 'conference_room') {
                    item = this.availableConferenceRooms.find(i => i.id === id);
                } else if (type === 'bookable') {
                    item = this.availableBookables.find(i => i.id === id);
                }
            } else {
                if (type === 'desk') {
                    item = this.unavailableDesks.find(i => i.id === id);
                } else if (type === 'conference_room') {
                    item = this.unavailableConferenceRooms.find(i => i.id === id);
                } else if (type === 'bookable') {
                    item = this.unavailableBookables.find(i => i.id === id);
                }
            }

            if (!item) return;

            this.openDialog(item, isAvailable ? 'available' : 'unavailable');
        },

        openDialog(item, type) {
            this.dialogItem = item;
            this.dialogType = type;
            this.showDialog = true;
        },

        closeDialog() {
            this.showDialog = false;
            this.dialogItem = null;
            this.dialogType = null;
        },

        navigateTo(url) {
            if (url) {
                window.location.href = url;
            }
        }
    }
};
</script>

<style>
#live-map {
    background: white;
}

.leaflet-control {
    display: none;
}

.leaflet-tooltip {
    padding: 0 !important;
    margin: 0 !important;
}

.checkedIn {
    background-color: #3fff33 !important;
    border: 1px solid #3fff33 !important;
}

.avatar-div {
    display: inline-flex;
    align-items: center;
    padding: 0;
    margin: 0;
    width: 20px;
    height: 20px;
    font-size: 12px;
    text-align: center;
}

.myTooltip {
    background: white;
    border: 1px solid #ccc;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 11px;
    white-space: nowrap;
    pointer-events: auto;
}
</style>
